// @flow

import React, { type Node, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { InputMask, formatValueWithMask } from '@tectonics/components/input-mask';
import { useUniqueId } from '@tectonics/hooks';
import { useUserInputAsTextFieldWithFormatting } from '@tectonics/prs/user-input-values';
import { useSubmitting } from '@tectonics/prs/submitting';
import { type CardBrand } from '@tectonics/prs/card-brand';
import { useSelectedCardBrand } from '@tectonics/look-and-feel/commons/selected-card-brand';
import { Label, TextField } from '../../../../../../commons/atoms';
import { InputGroup } from '../../../../../../commons/molecules';
import { ErrorLabel, InputHolder } from '../../commons';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { CreditCardIcon } from './credit-card-icon';
import { getCountry, useIncomingPayment } from '@tectonics/prs/incoming-payment';
import type { Country } from '@tectonics/prs/country';
import { translate } from '@tectonics/translate';

type CardMask = ImmutableArray<RegExp | string>;

export function InputCardNumber(): Node {
  const inputId = useUniqueId();
  const submitting = useSubmitting();
  const cardBrand = useSelectedCardBrand();
  const cardMask = useMemo(() => cardBrandMask(cardBrand), [cardBrand]);
  const formatRaw = useCallback((rawValue: string) => formatValueWithMask(rawValue, cardMask), [cardMask]);
  const [value, handleChange] = useUserInputAsTextFieldWithFormatting('cardNumber', formatRaw, parseFormatted);
  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('cardNumber');
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  return (
    <InputHolder data-test-name="card-number">
      <InputGroup
        label={
          <Label htmlFor={inputId}>
            <LabelMessage />
          </Label>}
        input={
          <InputFieldWrapper>
            <TextField
              placeholder="0000 0000 0000 0000"
              as={InputMask}
              id={inputId}
              type="tel"
              mask={cardBrandMask(cardBrand)}
              value={value}
              onChange={handleChange}
              fashion={formHasBeenSent && validationError ? 'error' : null}
              readOnly={submitting}
              data-error={formHasBeenSent && validationError ? 'error' : null}
              data-secret
              name="card-number"
            />
            <IconWrapper>
              <CreditCardIcon brand={cardBrand} />
            </IconWrapper>
          </InputFieldWrapper>
        }
      />
      {formHasBeenSent && validationError && (
        <ValidationErrorMessage country={country} validationError={validationError} />
      )}
    </InputHolder>
  );
}

function LabelMessage(): Node {
  return translate('shopify.inputCardNumber');
}

const InputFieldWrapper = styled.div`
  position: relative;
  & > * {
    width: 100%;
  }
  & > span {
    width: auto;
  }
`;

const IconWrapper = styled.span`
  position: absolute;
  pointer-events: none;
  right: 15px;
  top: 50%;
  margin-top: -10px;
`;

type ValidationErrorMessageProps = {|
  country: Country,
  validationError: UserInputValidationError,
|};

const messages = (validationError: UserInputValidationError) => {
  switch (validationError.id) {
    case 'empty':
      return {
        br: 'Informe o número do cartão',
        mx: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        ar: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        co: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        cl: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        pe: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        ec: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        bo: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        uy: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        py: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        cr: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        pa: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        sv: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        gt: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        do: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        za: 'Please insert your card number',
        ke: 'Please insert your card number',
        ng: 'Please insert your card number',
        in: 'Please insert your card number',
        tz: 'Please insert your card number',
        ug: 'Please insert your card number',
        zm: 'Please insert your card number',
        eg: 'Please insert your card number',
        gh: 'Please insert your card number',
        jm: 'Please insert your card number',
      };

    default:
      return {
        br: 'Número de cartão inválido',
        mx: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        ar: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        co: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        cl: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        pe: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        ec: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        bo: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        uy: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        py: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        cr: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        gt: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        sv: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        pa: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        do: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        za: 'Invalid card number',
        ke: 'Invalid card number',
        ng: 'Invalid card number',
        in: 'Invalid card number',
        tz: 'Invalid card number',
        ug: 'Invalid card number',
        zm: 'Invalid card number',
        eg: 'Invalid card number',
        gh: 'Invalid card number',
        jm: 'Invalid card number',
      };
  }
};

function ValidationErrorMessage({ country, validationError }: ValidationErrorMessageProps): Node {
  return (
    <ErrorLabel>
      {messages(validationError)[country] || (serverErrorMessage(validationError.payload))}
    </ErrorLabel>
  );
}

function parseFormatted(formattedValue: string): string {
  return formattedValue.replace(/[^0-9]+/ug, '');
}

function cardBrandMask(cardBrand: ?CardBrand): CardMask {
  switch (cardBrand) {
    case 'amex':
      return AMEX_MASK;

    case 'diners':
      return DINERS_MASK;

    default:
      return DEFAULT_MASK;
  }
}

const AMEX_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
  ' ',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
  ' ',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];

const DINERS_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
  ' ',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
  ' ',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];

const DEFAULT_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
  ' ',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
  ' ',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
  ' ',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];
